import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TransactionalOrderUI } from '@/api/TransactionalOrdersApi';
import { useTransactionalOrders } from '@/context/TransactionalOrdersContext';
import { formatCurrency } from '@/utils/formatCurrency';

type CreateTransactionFromOrderDialogProps = {
  order: TransactionalOrderUI | null;
  open: boolean;
  onClose: () => void;
};

export const CreateTransactionFromOrderDialog = ({ order, open, onClose }: CreateTransactionFromOrderDialogProps): React.ReactNode => {
  const { createTransactionFromOrder } = useTransactionalOrders();

  const [type, setType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      // amount > 0 → deposit, amount < 0 → withdrawal
      setType(order.amount > 0 ? 'deposit' : 'withdrawal');
      setAmount(Math.abs(order.amount));

      const orderDate = new Date(order.time * 1000);
      setDate(orderDate.toISOString().split('T')[0]);
    }
  }, [order]);

  const handleSave = async () => {
    if (!order) return;

    setSaving(true);
    try {
      await createTransactionFromOrder(order.id, type, amount, new Date(date));
      onClose();
    } catch (err) {
      console.error('Failed to create transaction:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Transaction from Broker Order</DialogTitle>
          {order && (
            <p className="text-sm text-muted-foreground">
              Creating a funds transaction based on broker order #{order.ticket} ({formatCurrency(Math.abs(order.amount))})
            </p>
          )}
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="create-date">Date</Label>
            <Input
              id="create-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-type">Type</Label>
            <Select value={type} onValueChange={(value: 'deposit' | 'withdrawal') => setType(value)}>
              <SelectTrigger id="create-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deposit">Deposit</SelectItem>
                <SelectItem value="withdrawal">Withdrawal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-amount">Amount</Label>
            <Input
              id="create-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || amount <= 0}>
            {saving ? 'Creating...' : 'Create & Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
